import { Button, Box, Typography, Paper, Skeleton, Divider, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { AddRoleModal } from "./AddRoleModal";
import RoleDetailsModal from "./RoleDetailsModal";
import { Role } from "../../types";
import RoleList from "./RoleList";
import { useRole } from "app/context/RoleContext";
import AddIcon from '@mui/icons-material/Add';
import SecurityIcon from '@mui/icons-material/Security';

interface RoleDetailsModalState {
  data: Role;
  status: boolean;
}

const RolesTab = () => {
  const { roles, loading: isLoading, getAllRoles } = useRole();
  const [isAddRoleModal, setIsAddRoleModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const theme = useTheme();
  const [isRoleDetailsModalOpen, setIsRoleDetailModalOpen] = useState<RoleDetailsModalState>({
    data: {
      _id: '',
      name: '',
      scopes: [],
    },
    status: false,
  });

  // Fetch roles when component mounts or refreshKey changes
  useEffect(() => {
    getAllRoles();
  }, [refreshKey]); 

  const handleAddRoleModalOpen = () => {
    setIsAddRoleModal(true);
  };

  const handleRoleClick = (role: Role) => {
    setIsRoleDetailModalOpen({
      data: role,
      status: true,
    });
  };

  const handleClose = () => {
    setIsRoleDetailModalOpen({
      data: {
        _id: '',
        name: '',
        scopes: [],
      },
      status: false,
    });
  };

  return (
    <Box 
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        height: "100%",
        padding: { xs: 1, sm: 2 }
      }}
    >
      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SecurityIcon 
            sx={{ 
              color: theme.palette.secondary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.secondary.main
              }}
            >
              Role Management
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              Create and manage user access roles
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAddRoleModalOpen}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            py: 1,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: '#0a1f3a',
              boxShadow: '0 4px 10px rgba(15, 40, 76, 0.2)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          Add New Role
        </Button>
      </Paper>
      
      <Divider sx={{ opacity: 0.5 }} />

      <Box sx={{ flexGrow: 1, minHeight: 300 }}>
        {isLoading ? (
          <Box sx={{ pt: 1, px: 2 }}>
            <RoleList roles={[]} onRoleClick={() => {}} loading={true}/>
          </Box>
        ) : roles && roles.length > 0 ? (
          <RoleList roles={roles} onRoleClick={handleRoleClick} key={refreshKey} />
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              py: 8,
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            <SecurityIcon sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>No Roles Created</Typography>
            <Typography variant="body2" sx={{ mb: 3, maxWidth: 400 }}>
              Create your first role to manage user permissions and access control
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleAddRoleModalOpen}
              startIcon={<AddIcon />}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                borderRadius: 1,
                borderWidth: 1.5,
                py: 1,
                px: 2.5,
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
                '&:hover': {
                  borderColor: theme.palette.secondary.dark,
                  backgroundColor: 'rgba(15, 40, 76, 0.04)',
                }
              }}
            >
              Create Role
            </Button>
          </Box>
        )}
      </Box>

      <AddRoleModal
        open={isAddRoleModal}
        onClose={() => {
          setIsAddRoleModal(false);
        }}
        setRefreshKey={setRefreshKey}
      />
      <RoleDetailsModal 
        key={`role-details-modal-${refreshKey}-${isRoleDetailsModalOpen.data._id}`}
        open={isRoleDetailsModalOpen.status} 
        onClose={handleClose} 
        data={isRoleDetailsModalOpen.data} 
        setRefreshKey={setRefreshKey}
      />
    </Box>
  );
};

export default RolesTab; 